import React, { useEffect, useState } from 'react';
import Input, { InputProps } from '../input';

interface PhoneInputProps extends Omit<InputProps, 'onChange'> {
  name: string;
  value?: string;
  onChange?: (name: string, value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

/**
 * Simple local phone formatter/mask component (no external library)
 * - formats to (123) 456-7890 while typing
 * - limits to 10 digits
 * - calls the parent's onChange with { target: { name, value } } shape which your handlers expect
 */
const PhoneInput: React.FC<PhoneInputProps> = ({ name, value, onChange, onBlur, placeholder, ...rest }) => {
  const formatPhone = (digits: string) => {
    if (!digits) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const [formattedValue, setFormattedValue] = useState<string>('');

  useEffect(() => {
    if (value) {
      const rawDigits = value.replace(/\D/g, '').slice(0, 10); // clean to 10 digits
      setFormattedValue(formatPhone(rawDigits));
    } else {
      setFormattedValue('');
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // get digits, limit to 10
    const rawDigits = e.target.value.replace(/\D/g, '').slice(0, 10);
    const formatted = formatPhone(rawDigits);
    setFormattedValue(formatted);

    onChange?.(name, formatted);
  };

  // Use the passed value (parent-controlled). If parent passed raw digits, it's OK — keep using formatted display.
  return (
    <Input
      {...rest}
      name={name}
      value={formattedValue ?? ''}
      onChange={handleChange}
      onBlur={onBlur}
      placeholder={placeholder ?? '(212) 555-1234'}
    />
  );
};

export default PhoneInput;
